import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, TextField
} from '@mui/material';
import { exchangeRateService } from '../../services/financeService';
import CsvActions from '../common/CsvActions';
import './Financial.css';

const fmtPKR = (v) =>
  v != null ? 'PKR ' + Number(v).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const today = () => new Date().toISOString().split('T')[0];

const RATE_HEADERS = ['Date', 'Bank Rate (USD→PKR)', 'Fee %', 'Net Rate', 'Bank Name', 'Notes'];
const buildRateRows = (rates) => rates.map(r => [
  r.rateDate || '', r.usdToPkr || '', r.bankFeePercent || 0, r.netRate || '',
  r.bankName || '', (r.notes || '').replace(/,/g, ';')
]);

const emptyForm = () => ({
  rateDate: today(),
  usdToPkr: '',
  bankFeePercent: '0',
  bankName: '',
  notes: '',
});

const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, danger = false }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent><p style={{ margin: 0, color: 'var(--color-neutral-700)' }}>{message}</p></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <button onClick={onCancel} className="btn-secondary">Cancel</button>
      <button onClick={onConfirm} className={danger ? 'btn-danger' : 'btn-primary'}>Confirm</button>
    </DialogActions>
  </Dialog>
);

const RateForm = ({ rate, onSave, onCancel }) => {
  const [form, setForm] = useState(rate ? {
    rateDate: rate.rateDate || today(),
    usdToPkr: rate.usdToPkr || '',
    bankFeePercent: rate.bankFeePercent || '0',
    bankName: rate.bankName || '',
    notes: rate.notes || '',
  } : emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const grossRate = parseFloat(form.usdToPkr) || 0;
  const feePercent = parseFloat(form.bankFeePercent) || 0;
  const netRate = grossRate > 0 ? grossRate * (1 - feePercent / 100) : 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.usdToPkr || parseFloat(form.usdToPkr) <= 0) { setError('Enter a valid rate'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        rateDate: form.rateDate,
        usdToPkr: parseFloat(form.usdToPkr),
        bankFeePercent: parseFloat(form.bankFeePercent) || 0,
        bankName: form.bankName || null,
        notes: form.notes || null,
      });
    } catch (err) {
      setError(err.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>{rate ? 'Edit Exchange Rate' : 'Add Exchange Rate'}</h3>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '0 20px 20px' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Date *</label>
                <input type="date" name="rateDate" value={form.rateDate} onChange={handleChange} className="form-input" required />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Bank</label>
                <input type="text" name="bankName" value={form.bankName} onChange={handleChange}
                  className="form-input" placeholder="e.g. HBL, MCB" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>USD to PKR Rate *</label>
                <input type="number" name="usdToPkr" value={form.usdToPkr} onChange={handleChange}
                  className="form-input" step="0.0001" min="0" required placeholder="e.g. 285.50" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Bank Fee %</label>
                <input type="number" name="bankFeePercent" value={form.bankFeePercent} onChange={handleChange}
                  className="form-input" step="0.01" min="0" max="10" placeholder="e.g. 0.50" />
              </div>
            </div>
            {grossRate > 0 && (
              <div style={{ background: 'var(--color-success-light)', border: '1px solid var(--color-success-border)', borderRadius: 8, padding: '10px 14px', marginTop: 14, fontSize: '0.85rem' }}>
                <div>Gross: <strong>PKR {grossRate.toFixed(4)}</strong> / USD</div>
                {feePercent > 0 && <div>Fee: <strong>{feePercent}%</strong> (PKR {(grossRate * feePercent / 100).toFixed(4)})</div>}
                <div style={{ fontWeight: 700, color: 'var(--color-success)', fontSize: '1rem', marginTop: 4 }}>
                  Net Rate: PKR {netRate.toFixed(4)} / USD
                </div>
              </div>
            )}
            <div className="form-group" style={{ margin: '14px 0 0' }}>
              <label>Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange}
                className="form-textarea" rows="2" placeholder="Optional" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : rate ? 'Update' : 'Add Rate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConverterTool = ({ latestRate }) => {
  const [usdAmount, setUsdAmount] = useState('');
  const grossRate = latestRate ? Number(latestRate.usdToPkr) : 0;
  const netRate = latestRate ? Number(latestRate.netRateAfterFee) : 0;
  const usd = parseFloat(usdAmount) || 0;

  return (
    <div style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ fontWeight: 700, marginBottom: 10, fontSize: '0.85rem', color: 'var(--color-neutral-700)' }}>Quick Converter</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 160 }}>
          <label style={{ fontSize: '0.8rem' }}>USD Amount</label>
          <input type="number" value={usdAmount} onChange={(e) => setUsdAmount(e.target.value)}
            className="form-input" placeholder="e.g. 50000" min="0" step="0.01" />
        </div>
        {usd > 0 && netRate > 0 && (
          <div style={{ flex: 2, minWidth: 200 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              At net rate PKR {netRate.toFixed(4)} / USD
              {latestRate?.bankFeePercent > 0 && ` (after ${latestRate.bankFeePercent}% fee)`}
            </div>
            <div style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--color-success)' }}>
              = {fmtPKR(usd * netRate)}
            </div>
            {latestRate?.bankFeePercent > 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                Gross: {fmtPKR(usd * grossRate)} — Bank fee: {fmtPKR(usd * grossRate - usd * netRate)}
              </div>
            )}
          </div>
        )}
        {!latestRate && <div style={{ color: 'var(--color-text-tertiary)', fontSize: '0.85rem' }}>Add a rate first to use converter</div>}
      </div>
    </div>
  );
};

const ExchangeRateManagement = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await exchangeRateService.getAll();
      if (res.success) setRates(res.data || []);
      else setError(res.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const handleSave = async (payload) => {
    if (editingRate) {
      const res = await exchangeRateService.update(editingRate.id, payload);
      if (!res.success) throw new Error(res.message);
    } else {
      const res = await exchangeRateService.create(payload);
      if (!res.success) throw new Error(res.message);
    }
    setShowForm(false);
    setEditingRate(null);
    showToast(editingRate ? 'Exchange rate updated' : 'Exchange rate added');
    fetchRates();
  };

  const handleDelete = async () => {
    try {
      await exchangeRateService.delete(confirmDelete.id);
      setConfirmDelete({ open: false, id: null });
      showToast('Exchange rate deleted');
      fetchRates();
    } catch (err) {
      setError(err.message);
      setConfirmDelete({ open: false, id: null });
    }
  };

  const latestRate = rates[0];

  if (loading) {
    return (
      <div className="financial-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading exchange rates...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="financial-dashboard">
      <div className="page-header">
        <div>
          <h2 style={{ margin: 0 }}>USD / PKR Exchange Rates</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            Track daily bank rates with fee deduction for accurate PKR conversion
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <CsvActions filename="exchange_rates" headers={RATE_HEADERS} matrix={buildRateRows(rates)} />
          <button onClick={() => { setEditingRate(null); setShowForm(true); }} className="btn-primary">+ Add Rate</button>
        </div>
      </div>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)', color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)', padding: '12px 20px', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.msg}
        </div>
      )}

      {/* Current Rate Banner */}
      {latestRate && (
        <div style={{
          background: 'linear-gradient(135deg, var(--color-text-primary), var(--color-primary))',
          borderRadius: 12, padding: '20px 24px', color: 'white', marginBottom: 20
        }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: 4 }}>Current Rate ({fmtDate(latestRate.rateDate)})</div>
          <div style={{ display: 'flex', gap: 30, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: '2rem', fontWeight: 800 }}>PKR {Number(latestRate.netRateAfterFee).toFixed(2)}</span>
              <span style={{ fontSize: '0.9rem', opacity: 0.7, marginLeft: 6 }}>/ USD (net)</span>
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              Gross: {Number(latestRate.usdToPkr).toFixed(4)}
              {latestRate.bankFeePercent > 0 && ` | Fee: ${latestRate.bankFeePercent}%`}
              {latestRate.bankName && ` | ${latestRate.bankName}`}
            </div>
          </div>
        </div>
      )}

      <ConverterTool latestRate={latestRate} />

      {rates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">—</div>
          <h3>No exchange rates recorded</h3>
          <p>Add your first USD/PKR rate to start tracking conversions</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Bank</th>
                <th style={{ textAlign: 'right' }}>Gross Rate</th>
                <th style={{ textAlign: 'right' }}>Fee %</th>
                <th style={{ textAlign: 'right' }}>Net Rate</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>{fmtDate(r.rateDate)}</td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{r.bankName || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.usdToPkr).toFixed(4)}</td>
                  <td style={{ textAlign: 'right', color: r.bankFeePercent > 0 ? 'var(--color-error)' : 'var(--color-text-tertiary)' }}>
                    {r.bankFeePercent > 0 ? `${r.bankFeePercent}%` : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--color-success)' }}>
                    {Number(r.netRateAfterFee).toFixed(4)}
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.notes || '—'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => { setEditingRate(r); setShowForm(true); }}
                        className="action-btn btn-secondary" style={{ fontSize: '0.75rem' }}>Edit</button>
                      <button onClick={() => setConfirmDelete({ open: true, id: r.id })}
                        className="action-btn btn-danger" style={{ fontSize: '0.75rem' }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <RateForm
          rate={editingRate}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingRate(null); }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete.open}
        title="Delete Exchange Rate"
        message="Delete this exchange rate entry? This cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
        danger
      />
    </div>
  );
};

export default ExchangeRateManagement;
